import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import * as types from "../actions/actionTypes";
import {
  getAllNotificationsResponse,
  getNotificationByIdResponse,
  getUnreadNotificationCountResponse,
  patchEntityList,
} from "../actions";
import { axiosGet, axiosPatch } from "../../utils/common/axios";
import {
  createEntitySagas,
  extractPayloadAndCallback,
  notifyError,
  runOptimisticAction,
  selectUserId,
  toQuery,
} from "./sagaHelpers";

export const notificationEntity = createEntitySagas({
  entity: "notification",
  endpoint: "notifications",
  label: "Notification",
  scope: "user",
  selectSlice: (state) => state.notification,
  listKey: "notifications",
  types: {
    getAll: types.GET_ALL_NOTIFICATIONS,
    getById: types.GET_NOTIFICATION_BY_ID,
    create: types.CREATE_NOTIFICATION,
    update: types.UPDATE_NOTIFICATION,
    remove: types.DELETE_NOTIFICATION,
  },
  actions: { getAllResponse: getAllNotificationsResponse, getByIdResponse: getNotificationByIdResponse },
});

const selectUnread = (state) => state.notification.unreadCount || 0;

function* unreadCountSaga(action) {
  const { payload } = extractPayloadAndCallback(action);
  try {
    const userId = yield* selectUserId(payload);
    if (!userId) return;
    const { data } = yield call(axiosGet, `notifications/unread-count${toQuery({ userId })}`);
    yield put(getUnreadNotificationCountResponse(Number(data) || 0));
  } catch (e) {
    // Badge count is non-critical; keep the last known value.
  }
}

// payload: { id }
function* markReadSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  const unread = yield select(selectUnread);
  yield put(getUnreadNotificationCountResponse(Math.max(0, unread - 1)));
  const data = yield* runOptimisticAction({
    entity: "notification",
    selectList: notificationEntity.selectList,
    id: payload.id,
    changes: { read: true },
    request: () => axiosPatch(`notifications/${payload.id}/read`),
    errorFallback: "Could not mark notification as read",
    callback,
  });
  if (!data) yield put(getUnreadNotificationCountResponse(unread));
}

function* markAllReadSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  const previous = yield select(notificationEntity.selectList);
  const unread = yield select(selectUnread);
  try {
    const userId = yield* selectUserId(payload);
    yield put(patchEntityList("notification", "mergeAll", { changes: { read: true } }));
    yield put(getUnreadNotificationCountResponse(0));
    yield call(axiosPatch, `notifications/mark-all-read${toQuery({ userId })}`);
    callback?.success?.();
  } catch (e) {
    yield put(getAllNotificationsResponse(previous));
    yield put(getUnreadNotificationCountResponse(unread));
    notifyError(e, "Could not mark all as read");
    callback?.failure?.(e);
  }
}

export function* notificationSaga() {
  yield all([
    call(notificationEntity.watcher),
    takeLatest(types.GET_UNREAD_NOTIFICATION_COUNT, unreadCountSaga),
    takeEvery(types.MARK_NOTIFICATION_READ, markReadSaga),
    takeLatest(types.MARK_ALL_NOTIFICATIONS_READ, markAllReadSaga),
  ]);
}
